import { useState } from "react";
import { Icon } from "@iconify/react";
import {
  ButtonPrimary,
  ButtonPrimary1,
  Error,
  HStack,
  VStack,
} from "../../../components/utils";
import { Input } from "../../../components/shared/InputField";
import {
  useAllocationMatrixStore,
  AllocationFactor,
} from "../../../store/useAllocationMatrixStore";
import {
  useGetPlatformFields,
  PlatformField,
} from "../../../queries/allocationMatrix";
import CreateFactorDialog from "../../../modals/CreateFactorDialog";

interface AllocationMatrixFactorSelectionProps {
  onStepChange: (e?: number) => void;
  onBackClick: () => void;
  readOnly?: boolean;
}

const CATEGORY_STYLES: Record<string, string> = {
  Employee: "bg-blue-50 text-blue-600",
  Grant: "bg-orange-50 text-orange-600",
  Org: "bg-purple-50 text-purple-600",
  Custom: "bg-gray-100 text-gray-600",
};

function CategoryBadge({ category }: { category?: string }) {
  if (!category) return null;
  const style = CATEGORY_STYLES[category] ?? "bg-gray-100 text-gray-600";
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${style}`}>
      {category}
    </span>
  );
}

const AllocationMatrixFactorSelection = (
  props: AllocationMatrixFactorSelectionProps
) => {
  const { onStepChange, onBackClick, readOnly = false } = props;
  const store = useAllocationMatrixStore();
  const [search, setSearch] = useState("");
  const [error, setError] = useState(false);

  const [createFactorOpen, setCreateFactorOpen] = useState(false);

  const { data: platformFields = [], isFetching } = useGetPlatformFields();

  const validSelectedFactors = store.selectedFactors.filter(
    (f) => f?.fieldPath && f?.name
  );
  const selectedPaths = new Set(validSelectedFactors.map((f) => f.fieldPath));

  const filtered = platformFields.filter((f) =>
    f.name.toLowerCase().includes(search.toLowerCase())
  );

  function handleAdd(field: PlatformField) {
    if (!selectedPaths.has(field.fieldPath)) {
      store.addFactor(field);
      setError(false);
    }
  }

  function handleRemove(fieldPath: string) {
    store.removeFactor(fieldPath);
  }

  function handleNext() {
    if (validSelectedFactors.length === 0) {
      setError(true);
      return;
    }
    onStepChange();
  }

  return (
    <VStack className="w-full h-full">
      {/* Header */}
      <VStack className="justify-between gap-1 pb-3 pl-6 font-medium border-b-[0.5px] pt-7">
        <HStack className="flex justify-between font-semibold text-lg1 text-black-501">
          Define Factors
        </HStack>
        <HStack className="flex font-medium text-sm3 text-gray-401">
          Pick attributes from Hissa or create a custom factor. Custom factors
          need an Excel data upload.
        </HStack>
      </VStack>

      {/* Body */}
      <HStack className="flex-1 gap-10 px-6 pt-6 pb-2 overflow-hidden">
        {/* Left — factor list */}
        <VStack className="flex-1 gap-4 overflow-hidden">
          <VStack className="gap-1">
            <p className="text-sm font-semibold text-black-501">Add factors</p>
            <p className="text-xs text-gray-401">
              Search Hissa fields, or create a custom factor if what you need
              isn&apos;t there.
            </p>
          </VStack>

          {/* Search + create */}
          <HStack className="gap-2">
            <HStack className="flex-1 items-center gap-2 px-3 border rounded-md bg-white">
              <Icon
                icon="ri:search-line"
                className="text-gray-400 flex-shrink-0"
                height={16}
              />
              <Input
                className="flex-1 border-0 shadow-none outline-none bg-transparent placeholder-gray-400"
                placeholder="Search factors..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </HStack>
            <ButtonPrimary1
              disabled={readOnly}
              className="flex items-center gap-1 text-sm whitespace-nowrap"
              onClick={() => setCreateFactorOpen(true)}
            >
              <Icon icon="ri:add-line" height={16} />
              Create new
            </ButtonPrimary1>
          </HStack>

          {/* Field rows */}
          <VStack className="flex-1 overflow-y-auto border rounded-md divide-y">
            {isFetching && (
              <p className="p-4 text-sm text-gray-400 text-center">
                Loading fields...
              </p>
            )}
            {!isFetching && filtered.length === 0 && (
              <p className="p-4 text-sm text-gray-400 text-center">
                No fields found.
              </p>
            )}
            {filtered.map((field) => {
              const isSelected = selectedPaths.has(field.fieldPath);
              return (
                <HStack
                  key={field.fieldPath}
                  className="items-center justify-between px-4 py-3 min-h-[56px]"
                >
                  <VStack className="gap-0.5">
                    <HStack className="items-center gap-2">
                      <span className="text-sm font-medium text-black-501">
                        {field.name}
                      </span>
                      <CategoryBadge
                        category={
                          field.category ??
                          (field.isCustom ? "Custom" : undefined)
                        }
                      />
                      {field.isPreviouslyUsed && (
                        <span className="text-xs text-gray-400">
                          previously used
                        </span>
                      )}
                    </HStack>
                  </VStack>
                  <button
                    type="button"
                    disabled={isSelected || readOnly}
                    onClick={() => handleAdd(field)}
                    className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                      isSelected || readOnly
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-orange-501 hover:text-orange-600 cursor-pointer"
                    }`}
                  >
                    <Icon icon="ri:add-line" height={16} />
                    Add
                  </button>
                </HStack>
              );
            })}
          </VStack>
        </VStack>

        {/* Right — selected panel */}
        <VStack className="flex-2 flex-shrink-0 gap-3">
          <HStack className="items-center justify-between">
            <span className="text-sm font-semibold text-black-501">
              Selected
            </span>
            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-xs font-medium text-gray-600">
              {validSelectedFactors.length}
            </span>
          </HStack>

          <VStack className="flex-1 border rounded-md p-3 gap-2 overflow-y-auto">
            {validSelectedFactors.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">
                Add at least one factor to continue.
              </p>
            ) : (
              validSelectedFactors.map((field) => (
                <HStack
                  key={field.fieldPath}
                  className="items-center justify-between px-3 py-2.5 bg-gray-50 rounded-md border border-gray-100 min-h-[56px]"
                >
                  <VStack className="flex-1 min-w-0">
                    <span className="text-sm font-semibold text-black-501 truncate">
                      {field.name}
                    </span>
                  </VStack>
                  <button
                    type="button"
                    disabled={readOnly}
                    onClick={() => handleRemove(field.fieldPath)}
                    className={`ml-2 mt-0.5 flex-shrink-0 ${
                      readOnly
                        ? "text-gray-200 cursor-not-allowed"
                        : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    <Icon icon="ri:close-line" height={16} />
                  </button>
                </HStack>
              ))
            )}
          </VStack>

          {error && <Error text="Add at least one factor to continue." />}

          <HStack className="items-center gap-1.5 text-xs text-gray-400">
            <Icon
              icon="ri:information-line"
              height={14}
              className="flex-shrink-0"
            />
            <span>
              New factors are saved to your Factor Registry for reuse.
            </span>
          </HStack>
        </VStack>
      </HStack>

      {/* Footer */}
      <HStack className="justify-between px-6 pt-4 pb-4 border-t-[0.5px]">
        <ButtonPrimary1 onClick={onBackClick}>
          <Icon
            icon="ri:arrow-left-s-line"
            height={16}
            className="inline mr-1"
          />
          Back
        </ButtonPrimary1>
        <ButtonPrimary onClick={handleNext}>
          Next
          <Icon
            icon="ri:arrow-right-s-line"
            height={16}
            className="inline ml-1"
          />
        </ButtonPrimary>
      </HStack>

      <CreateFactorDialog
        open={createFactorOpen}
        onClose={() => setCreateFactorOpen(false)}
        existingNames={platformFields.map((f) => f.name)}
        onFactorCreated={(factor: AllocationFactor) => {
          store.addFactor(factor);
          setError(false);
        }}
      />
    </VStack>
  );
};

export default AllocationMatrixFactorSelection;
